import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Layout from "../component/layout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Badge } from "primereact/badge";
import { Dropdown } from "primereact/dropdown";
import { toastify } from "../../../utils/toast";
import { getItem } from "../../../utils/localStorage";
import { makeRequest2 } from "../../../utils/makeRequest";
import BookingInvoiceDialog from "../booking_management/booking_invoices/BookingInvoiceDialog";
import {
    getPMRevenueStreamsURL,
    processPMRevenueStreamsURL,
    getPMRevenueReceiptURL,
    generatePMRevenueReportURL,
    backend_url
} from "../../../utils/urls";

// Updated: Only 3 revenue streams - removed rent_commission
const SOURCE_META = {
    booking_commission: { label: "Booking Commission", severity: "info", icon: "ti ti-calendar-event" },
    lease_fee: { label: "Lease Commission", severity: "warning", icon: "ti ti-file-certificate" },
    management_fee: { label: "Management Commission", severity: "danger", icon: "ti ti-briefcase" }
};

const PropertyManagerRevenue = () => {
    const [managers, setManagers] = useState([]);
    const [selectedManagerId, setSelectedManagerId] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [totals, setTotals] = useState(null);
    const [managerBreakdown, setManagerBreakdown] = useState([]);
    const [entries, setEntries] = useState([]);

    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [showProcessConfirm, setShowProcessConfirm] = useState(false);
    const [selectedBookingInvoiceId, setSelectedBookingInvoiceId] = useState(null);
    const [bookingInvoiceDialogVisible, setBookingInvoiceDialogVisible] = useState(false);

    const fetchRevenue = useCallback(async () => {
        try {
            setLoading(true);
            const facilityId = await getItem("selectedFacilityId");
            if (!facilityId) {
                toastify("No facility selected", "error");
                return;
            }

            const params = new URLSearchParams({ page: "1", limit: "200" });
            if (selectedManagerId) params.append("propertyManagerId", selectedManagerId);
            if (selectedSource) params.append("source", selectedSource);
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const url = `${getPMRevenueStreamsURL}/${facilityId}?${params.toString()}`;
            const response = await makeRequest2(url, "GET");

            if (response.success && response.data) {
                const data = response.data.data || response.data;
                setManagers(data.managers || []);
                setTotals(data.totals || null);
                setManagerBreakdown(data.managerBreakdown || []);
                setEntries(data.entries || []);
            } else {
                toastify(response.error || "Failed to fetch revenue data", "error");
                setTotals(null);
                setManagerBreakdown([]);
                setEntries([]);
            }
        } catch (error) {
            toastify(error.message, "error");
        } finally {
            setLoading(false);
        }
    }, [selectedManagerId, selectedSource, startDate, endDate]);

    const processRevenue = async () => {
        setShowProcessConfirm(false);
        try {
            setProcessing(true);
            const facilityId = await getItem("selectedFacilityId");
            const response = await makeRequest2(
                `${processPMRevenueStreamsURL}/${facilityId}`,
                "POST",
                {}
            );

            if (response.success) {
                const data = response.data?.data || response.data || {};
                if (data.entriesCreated > 0) {
                    toastify(
                        `Processed ${data.entriesCreated} new revenue entries (KES ${Number(data.totalRevenue || 0).toLocaleString()})`,
                        "success"
                    );
                } else {
                    toastify(data.message || "No new revenue to process", "info");
                }
                fetchRevenue();
            } else {
                toastify(response.error || "Processing failed", "error");
            }
        } catch (error) {
            toastify(error.message, "error");
        } finally {
            setProcessing(false);
        }
    };

    useEffect(() => {
        fetchRevenue();
    }, [fetchRevenue]);

    const formatCurrency = (amount) => {
        const numAmount = Number(amount);
        if (amount === null || amount === undefined || isNaN(numAmount)) return "KES 0.00";
        return `KES ${numAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
            });
        } catch (error) {
            return "Invalid Date";
        }
    };

    const managerOptions = [
        { label: "All Property Managers", value: null },
        ...managers.map((manager) => ({
            label: manager.fullName || manager.email || "Unknown",
            value: manager._id
        }))
    ];

    const sourceOptions = [
        { label: "All Revenue Sources", value: null },
        ...Object.entries(SOURCE_META).map(([value, meta]) => ({
            label: meta.label,
            value
        }))
    ];

    const sourceTemplate = (rowData) => {
        const meta = SOURCE_META[rowData.source] || { label: rowData.source, severity: "secondary" };
        return <Badge value={meta.label} severity={meta.severity} />;
    };

    const managerTemplate = (rowData) => {
        const manager = rowData.propertyManager;
        if (!manager) return <span className="text-muted">No Manager</span>;
        return <div className="fw-bold">{manager.fullName || "Unknown"}</div>;
    };

    const rateTemplate = (rowData) => {
        if (!rowData.rate || rowData.rate.value === undefined) return "N/A";
        return rowData.rate.type === "percentage"
            ? `${rowData.rate.value}%`
            : formatCurrency(rowData.rate.value);
    };

    const referenceTemplate = (rowData) => {
        if (rowData.reference?.kind === "invoice") {
            return (
                <Link
                    to={`/facility/levy_management/invoice/${rowData.reference.id}`}
                    className="text-primary"
                    style={{ textDecoration: "none" }}
                >
                    {rowData.reference.number || "Invoice"}
                </Link>
            );
        }
        if (rowData.reference?.kind === "booking_invoice" || rowData.reference?.kind === "reservation") {
            return (
                <Button
                    label={rowData.reference.number || "Booking"}
                    link
                    className="p-0 text-primary"
                    style={{ textDecoration: "none" }}
                    onClick={() => {
                        setSelectedBookingInvoiceId(rowData.reference.id);
                        setBookingInvoiceDialogVisible(true);
                    }}
                />
            );
        }
        return <span className="text-muted">{rowData.reference?.number || "Booking"}</span>;
    };

    const exportEntriesCSV = () => {
        if (entries.length === 0) {
            toastify("No data to export", "warning");
            return;
        }
        const csvData = entries.map((entry) => ({
            Date: formatDate(entry.earnedDate),
            "Property Manager": entry.propertyManager?.fullName || "N/A",
            Unit: entry.unitName || "N/A",
            Client: entry.client?.name || "N/A",
            Source: SOURCE_META[entry.source]?.label || entry.source,
            "Base Amount": entry.baseAmount,
            Rate: entry.rate?.type === "percentage" ? `${entry.rate.value}%` : entry.rate?.value,
            Revenue: entry.amount,
            Reference: entry.reference?.number || ""
        }));
        const csvContent =
            "data:text/csv;charset=utf-8," +
            Object.keys(csvData[0]).join(",") + "\n" +
            csvData.map((row) => Object.values(row).join(",")).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `property_manager_revenue_${new Date().toISOString().split("T")[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toastify("Revenue entries exported to CSV", "success");
    };

    // Binary downloads (PDF receipt, xlsx report) go through fetch directly
    // since makeRequest2 is JSON-only — same Bearer auth, just a blob response.
    const downloadFile = async (url, filename) => {
        try {
            const appUser = await getItem("APPUSER");
            if (!appUser?.authToken) {
                toastify("Authentication token not found", "error");
                return;
            }
            const response = await fetch(`${backend_url}${url}`, {
                headers: { Authorization: `Bearer ${appUser.authToken}` }
            });
            if (!response.ok) {
                throw new Error(`Download failed (${response.status})`);
            }
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            toastify(error.message || "Download failed", "error");
        }
    };

    const downloadReport = async () => {
        const facilityId = await getItem("selectedFacilityId");
        const params = new URLSearchParams();
        if (selectedManagerId) params.append("propertyManagerId", selectedManagerId);
        if (selectedSource) params.append("source", selectedSource);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        await downloadFile(
            `${generatePMRevenueReportURL}/${facilityId}?${params.toString()}`,
            `revenue-report-${new Date().toISOString().split("T")[0]}.xlsx`
        );
    };

    const downloadReceipt = async (entry) => {
        const facilityId = await getItem("selectedFacilityId");
        await downloadFile(
            `${getPMRevenueReceiptURL}/${facilityId}/${entry._id}`,
            `receipt-${entry.reference?.number || entry._id}.pdf`
        );
    };

    // Updated: Only 3 summary cards (removed rent_commission)
    const summaryCard = (sourceKey, borderClass, textClass) => {
        const meta = SOURCE_META[sourceKey];
        const stats = totals?.bySource?.[sourceKey] || { amount: 0, count: 0 };
        return (
            <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                <div className={`card ${borderClass} shadow-sm h-100`}>
                    <div className="card-body text-center">
                        <i className={`${meta.icon} ${textClass} mb-2`} style={{ fontSize: "2rem" }}></i>
                        <h4 className={`${textClass} mb-1`}>{formatCurrency(stats.amount)}</h4>
                        <h6 className="text-muted mb-2">{meta.label}</h6>
                        <small className="text-muted">
                            {Number(stats.count || 0).toLocaleString()} entr{stats.count === 1 ? "y" : "ies"}
                        </small>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/core/dashboard/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="">Property Manager</Link>
                                </li>
                                <li className="breadcrumb-item">Revenue</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Property Manager Revenue</h5>
                        </div>
                        <div className="card-body">
                            {/* Filters */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="form-label">Property Manager</label>
                                    <Dropdown
                                        value={selectedManagerId}
                                        options={managerOptions}
                                        onChange={(e) => setSelectedManagerId(e.value)}
                                        placeholder="All Property Managers"
                                        className="w-100"
                                        filter
                                        showClear={!!selectedManagerId}
                                        disabled={loading || processing}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Revenue Source</label>
                                    <Dropdown
                                        value={selectedSource}
                                        options={sourceOptions}
                                        onChange={(e) => setSelectedSource(e.value)}
                                        placeholder="All Sources"
                                        className="w-100"
                                        disabled={loading || processing}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        disabled={loading || processing}
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        disabled={loading || processing}
                                    />
                                </div>
                                <div className="col-md-3 d-flex align-items-end gap-2">
                                    <Button
                                        label={loading ? "Loading..." : "Refresh"}
                                        icon={loading ? "ti ti-loader animate-spin" : "ti ti-refresh"}
                                        className="p-button-primary"
                                        onClick={fetchRevenue}
                                        disabled={loading || processing}
                                    />
                                    <Button
                                        label={processing ? "Processing..." : "Process Revenue"}
                                        icon={processing ? "ti ti-loader animate-spin" : "ti ti-play"}
                                        className="p-button-success"
                                        onClick={() => setShowProcessConfirm(true)}
                                        disabled={loading || processing}
                                        tooltip="Generate revenue entries from paid rent invoices and bookings"
                                        tooltipOptions={{ position: "bottom" }}
                                    />
                                </div>
                            </div>

                            {/* Updated: 3 summary cards + total card (removed rent_commission) */}
                            <div className="row mb-3">
                                {summaryCard("booking_commission", "border-info", "text-info")}
                                {summaryCard("lease_fee", "border-warning", "text-warning")}
                                {summaryCard("management_fee", "border-danger", "text-danger")}
                                <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                    <div className="card border-primary shadow-sm h-100">
                                        <div className="card-body text-center">
                                            <i className="ti ti-trending-up text-primary mb-2" style={{ fontSize: "2rem" }}></i>
                                            <h4 className="text-primary mb-1">
                                                {formatCurrency(totals?.totalRevenue || 0)}
                                            </h4>
                                            <h6 className="text-muted mb-2">Total Revenue</h6>
                                            <small className="text-muted">
                                                {Number(totals?.totalEntries || 0).toLocaleString()} entries
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Per-manager breakdown - Updated columns (removed rentCommission) */}
                            {!selectedManagerId && managerBreakdown.length > 0 && (
                                <div className="card mb-3">
                                    <div className="card-header">
                                        <h6 className="mb-0">
                                            <i className="ti ti-users me-2"></i>
                                            Revenue by Property Manager
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <DataTable
                                            value={managerBreakdown}
                                            stripedRows
                                            showGridlines
                                            responsiveLayout="scroll"
                                            dataKey="managerId"
                                            emptyMessage="No revenue recorded yet"
                                        >
                                            <Column
                                                header="Property Manager"
                                                body={(row) => (
                                                    <div className="fw-bold">{row.managerName || "Unknown"}</div>
                                                )}
                                                style={{ minWidth: "200px" }}
                                            />
                                            <Column
                                                field="bookingCommission"
                                                header="Booking Commission"
                                                body={(row) => formatCurrency(row.bookingCommission)}
                                                sortable
                                                style={{ textAlign: "right", minWidth: "160px" }}
                                            />
                                            <Column
                                                field="leaseFee"
                                                header="Lease Commission"
                                                body={(row) => formatCurrency(row.leaseFee)}
                                                sortable
                                                style={{ textAlign: "right", minWidth: "140px" }}
                                            />
                                            <Column
                                                field="managementFee"
                                                header="Management Commission"
                                                body={(row) => formatCurrency(row.managementFee)}
                                                sortable
                                                style={{ textAlign: "right", minWidth: "150px" }}
                                            />
                                            <Column
                                                field="totalRevenue"
                                                header="Total Revenue"
                                                body={(row) => (
                                                    <span className="fw-bold text-success">
                                                        {formatCurrency(row.totalRevenue)}
                                                    </span>
                                                )}
                                                sortable
                                                style={{ textAlign: "right", minWidth: "150px" }}
                                            />
                                            <Column
                                                header=""
                                                body={(row) => (
                                                    <Button
                                                        icon="ti ti-filter"
                                                        className="p-button-sm p-button-outlined"
                                                        tooltip="View this manager's entries"
                                                        tooltipOptions={{ position: "top" }}
                                                        onClick={() => setSelectedManagerId(row.managerId)}
                                                    />
                                                )}
                                                style={{ width: "70px", textAlign: "center" }}
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            )}

                            {/* Revenue entries */}
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                        <i className="ti ti-table me-2"></i>
                                        Revenue Entries
                                        <Badge
                                            value={`${entries.length} shown`}
                                            severity="info"
                                            className="ms-2"
                                        />
                                    </h6>
                                    <div className="d-flex gap-2">
                                        <Button
                                            label="Export CSV"
                                            icon="ti ti-file-export"
                                            className="p-button-outlined p-button-sm"
                                            onClick={exportEntriesCSV}
                                            disabled={entries.length === 0}
                                        />
                                        <Button
                                            label="Download Report"
                                            icon="ti ti-file-spreadsheet"
                                            className="p-button-outlined p-button-sm"
                                            onClick={downloadReport}
                                            disabled={entries.length === 0}
                                            tooltip="Download a formatted .xlsx revenue report for the current filters"
                                            tooltipOptions={{ position: "top" }}
                                        />
                                    </div>
                                </div>
                                <div className="card-body">
                                    <DataTable
                                        value={entries}
                                        loading={loading}
                                        emptyMessage="No revenue entries found. Click 'Process Revenue' to generate revenue from paid rent invoices and bookings."
                                        stripedRows
                                        showGridlines
                                        paginator
                                        rows={10}
                                        sortMode="multiple"
                                        removableSort
                                        dataKey="_id"
                                        responsiveLayout="scroll"
                                        tableStyle={{ minWidth: "70rem" }}
                                    >
                                        <Column
                                            field="earnedDate"
                                            header="Date"
                                            body={(row) => formatDate(row.earnedDate)}
                                            sortable
                                            style={{ minWidth: "120px" }}
                                        />
                                        <Column
                                            header="Property Manager"
                                            body={managerTemplate}
                                            style={{ minWidth: "180px" }}
                                        />
                                        <Column
                                            field="unitName"
                                            header="Unit"
                                            body={(row) => (
                                                <Link
                                                    to={`/facility/unit_management/view_unit/${row.unit}`}
                                                    className="text-primary fw-bold"
                                                    style={{ textDecoration: "none" }}
                                                >
                                                    {row.unitName || "N/A"}
                                                </Link>
                                            )}
                                            sortable
                                            style={{ minWidth: "140px" }}
                                        />
                                        <Column
                                            header="Client"
                                            body={(row) => row.client?.name || <span className="text-muted">N/A</span>}
                                            style={{ minWidth: "150px" }}
                                        />
                                        <Column
                                            field="source"
                                            header="Source"
                                            body={sourceTemplate}
                                            sortable
                                            style={{ minWidth: "150px" }}
                                        />
                                        <Column
                                            field="baseAmount"
                                            header="Base Amount"
                                            body={(row) => formatCurrency(row.baseAmount)}
                                            sortable
                                            style={{ textAlign: "right", minWidth: "130px" }}
                                        />
                                        <Column
                                            header="Rate"
                                            body={rateTemplate}
                                            style={{ textAlign: "right", minWidth: "90px" }}
                                        />
                                        <Column
                                            field="amount"
                                            header="Revenue"
                                            body={(row) => (
                                                <span className="fw-bold text-success">
                                                    {formatCurrency(row.amount)}
                                                </span>
                                            )}
                                            sortable
                                            style={{ textAlign: "right", minWidth: "130px" }}
                                        />
                                        <Column
                                            header="Reference"
                                            body={referenceTemplate}
                                            style={{ minWidth: "130px" }}
                                        />
                                        <Column
                                            header="Receipt"
                                            body={(row) => (
                                                <Button
                                                    icon="ti ti-receipt"
                                                    className="p-button-sm p-button-outlined p-button-secondary"
                                                    tooltip="Download receipt"
                                                    tooltipOptions={{ position: "top" }}
                                                    onClick={() => downloadReceipt(row)}
                                                />
                                            )}
                                            style={{ width: "80px", textAlign: "center" }}
                                        />
                                    </DataTable>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Process revenue confirmation dialog */}
            <Dialog
                header="Process Property Manager Revenue"
                visible={showProcessConfirm}
                style={{ width: "480px", maxWidth: "95vw" }}
                onHide={() => setShowProcessConfirm(false)}
            >
                <p className="mb-3">
                    This scans paid rent, management fee and booking invoices on
                    managed units and records revenue for:
                </p>
                <ul className="mb-3">
                    <li className="mb-2">
                        <Badge value="Booking Commission" severity="info" className="me-2" />
                        from paid bookings
                    </li>
                    <li className="mb-2">
                        <Badge value="Lease Commission" severity="warning" className="me-2" />
                        on the first rent payment per tenant
                    </li>
                    <li className="mb-2">
                        <Badge value="Management Commission" severity="danger" className="me-2" />
                        on every paid Property-Management invoice
                    </li>
                </ul>
                <div className="alert alert-info py-2 mb-3">
                    <small>
                        <i className="ti ti-shield-check me-1"></i>
                        Each payment or booking is only ever processed once — running
                        this again never duplicates revenue.
                    </small>
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <Button
                        label="Cancel"
                        className="p-button-outlined"
                        onClick={() => setShowProcessConfirm(false)}
                    />
                    <Button
                        label="Process Revenue"
                        icon="ti ti-play"
                        className="p-button-success"
                        onClick={processRevenue}
                    />
                </div>
            </Dialog>

            <BookingInvoiceDialog
                visible={bookingInvoiceDialogVisible}
                onHide={() => {
                    setBookingInvoiceDialogVisible(false);
                    setSelectedBookingInvoiceId(null);
                }}
                invoiceId={selectedBookingInvoiceId}
            />
        </Layout>
    );
};

export default PropertyManagerRevenue;